'use client';
import { withAuth } from '@/components/auth/WithAuth';
import style from './PageClient.module.scss';
import { COINS } from '@/constants/coin';
import Image from 'next/image';
import { IconHelpOutline } from '@/components/icons';
import PageContainer from '@/components/layout/PageContainer';
import { useSetMobileHeader } from '@/components/layout/MobileHeaderProvider';
import MobileHeader from '@/components/header/me-coin-wallet/Header';
import HeaderContent from '@/components/header/me-coin-wallet/HeaderContent';
import { useEffect, useState, useCallback } from 'react';
import GreenCoinCollectList from './GreenCoinCollectList';
import RedCoinCollectList from './RedCoinCollectList';
import { WalletService } from '@/libs/services/wallet';
import { showApiError } from '@/libs/helpers/showApiState';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import { MyWalletData } from '@/types/wallet';
import BottomNav from '@/components/bottomNav/BottomNav';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useQueryEnumParam } from '@/hooks/useQueryEnumParam';
import * as Popover from '@radix-ui/react-popover';

type TabType = 'greenCoin' | 'redCoin' | 'goldCoin';

const EMPTY_WALLET: MyWalletData = {
  free_coin: 0,
  pay_coin: 0,
  womo: 0,
};

const PageClient = () => {
  const mounted = useIsMounted();

  useSetMobileHeader(MobileHeader);

  const [tab, setTab] = useQueryEnumParam<TabType>({
    paramName: 'tabPage',
    validValues: ['greenCoin', 'redCoin', 'goldCoin'] as const,
    defaultValue: 'greenCoin',
  });
  const [myWalletData, setMyWalletData] = useState<MyWalletData>(EMPTY_WALLET);
  const dispatch = useAppDispatch();
  const getWalletInfo = useCallback(async () => {
    dispatch(startGlobalLoading());
    try {
      const result = await WalletService.getMyWallet();
      setMyWalletData(result ?? EMPTY_WALLET);
    } catch (err) {
      showApiError(err, '取得籌碼包資訊失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    getWalletInfo();
  }, [getWalletInfo]);

  if (!mounted) {
    return null;
  }

  return (
    <>
      <PageContainer maxWidth="xl">
        {/* 桌機顯示區 */}
        <div className="d-none d-md-block mb-20">
          <HeaderContent />
        </div>

        <div>
          {/* 頁籤區 */}
          <section className={style.walletHeader}>
            <div className={`${style.coinTab}`}>
              <button
                type="button"
                className={style.coinTabBtn}
                onClick={() => setTab('greenCoin')}
                style={{
                  borderBottom: `3px solid  ${tab === 'greenCoin' ? '#000' : ' transparent'}`,
                }}
              >
                <Image src={COINS[0].image} alt={COINS[0].name} width={48} height={48} />
                <span className="text-coinGreen fw-bold fs-5">{myWalletData?.free_coin}</span>
              </button>
              <button
                type="button"
                className={style.coinTabBtn}
                onClick={() => setTab('redCoin')}
                style={{
                  borderBottom: `3px solid  ${tab === 'redCoin' ? '#000' : ' transparent'}`,
                }}
              >
                <Image src={COINS[1].image} alt={COINS[1].name} width={48} height={48} />
                <span className="text-coinRed fw-bold fs-5">{myWalletData?.pay_coin}</span>
              </button>
              <button
                type="button"
                className={style.coinTabBtn}
                onClick={() => setTab('goldCoin')}
                style={{
                  borderBottom: `3px solid  ${tab === 'goldCoin' ? '#000' : ' transparent'}`,
                }}
              >
                <Image src={COINS[2].image} alt={COINS[2].name} width={48} height={48} />
                <span className="text-coinGold fw-bold fs-5">{myWalletData?.womo}</span>
              </button>
            </div>
          </section>

          {/* 數據區 */}
          <section className="bg-grey-50 px-5 py-5 d-flex justify-content-between align-items-center">
            {tab === 'greenCoin' ? (
              <div className="fw-bold d-flex align-items-center">
                <p>
                  已蒐集綠籌碼：
                  <span className="px-1 text-coinGreen">{myWalletData?.free_coin}</span>顆
                </p>
                <Popover.Root>
                  <Popover.Trigger className={style.popoverTrigger}>
                    <IconHelpOutline width={20} />
                  </Popover.Trigger>

                  <Popover.Content sideOffset={8} className={style.popoverContent}>
                    <h6 className="text-green fw-bold mb-1 fs-sm-2">{COINS[0].title}</h6>
                    <p className="fs-xs">{COINS[0].description}</p>
                    <Popover.Arrow className={style.popoverArrow} />
                  </Popover.Content>
                </Popover.Root>
              </div>
            ) : tab === 'redCoin' ? (
              <div className="fw-bold d-flex align-items-center">
                <p>
                  已蒐集紅籌碼：<span className="px-1 text-coinRed">{myWalletData?.pay_coin}</span>
                  顆
                </p>
                <Popover.Root>
                  <Popover.Trigger className={style.popoverTrigger}>
                    <IconHelpOutline width={20} />
                  </Popover.Trigger>

                  <Popover.Content sideOffset={8} className={style.popoverContent}>
                    <h6 className="text-red fw-bold mb-1 fs-sm-2">{COINS[1].title}</h6>
                    <p className="fs-xs">{COINS[1].description}</p>
                    <Popover.Arrow className={style.popoverArrow} />
                  </Popover.Content>
                </Popover.Root>
              </div>
            ) : (
              <div className="fw-bold d-flex align-items-center">
                <p>
                  剩餘金籌碼：<span className="px-1 text-coinGold">{myWalletData?.womo}</span>顆
                </p>
                <Popover.Root>
                  <Popover.Trigger className={style.popoverTrigger}>
                    <IconHelpOutline width={20} />
                  </Popover.Trigger>

                  <Popover.Content sideOffset={8} className={style.popoverContent}>
                    <h6 className="text-gold fw-bold mb-1 fs-sm-2">{COINS[2].title}</h6>
                    <p className="fs-xs">{COINS[2].description}</p>
                    <Popover.Arrow className={style.popoverArrow} />
                  </Popover.Content>
                </Popover.Root>
              </div>
            )}

            {/* {tab === 'goldCoin' && (
              <Button as="a" href="/deposit" color="orange" size="sm">
                儲值
              </Button>
            )} */}
          </section>

          {/* 列表區 */}

          {tab === 'greenCoin' ? (
            <GreenCoinCollectList onReloadWallet={getWalletInfo} />
          ) : tab === 'redCoin' ? (
            <RedCoinCollectList />
          ) : (
            // <CoinTask />
            ''
          )}
        </div>
      </PageContainer>
      {/* 置底按鈕選單 */}
      <BottomNav />
    </>
  );
};

export default withAuth(PageClient);
